import { useCallback, useMemo, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'

import { myTableColumnPreferences } from '../../../graphql/queries/user'
import { resetTableColumnPreferences, updateTableColumnPreferences } from '../../../graphql/mutations/user'

/**
 * Same persisted column show/hide/reorder preferences as `useColumnPreferences`
 * (same GraphQL persistence, same `User.tableColumnPreferences` JSON blob,
 * same `ColumnSettingsButton` UI contract), but for plain custom tables that
 * render their own <Table> rather than react-admin's <Datagrid> -- driven by
 * an explicit `{field, label}[]` column list instead of introspecting
 * react-admin Field children via useResourceContext().
 */
export function useColumnPreferencesForTable(tableId, defaultColumnDefs) {
  const [localColumns, setLocalColumns] = useState(null)
  const [hasLocalChanges, setHasLocalChanges] = useState(false)

  const { data, loading, refetch } = useQuery(myTableColumnPreferences, {
    variables: { tableId },
    fetchPolicy: 'cache-and-network',
  })

  const [updatePreferences, { loading: saving }] = useMutation(updateTableColumnPreferences, {
    refetchQueries: [{ query: myTableColumnPreferences, variables: { tableId } }],
  })
  const [resetPreferences, { loading: resetting }] = useMutation(resetTableColumnPreferences, {
    refetchQueries: [{ query: myTableColumnPreferences, variables: { tableId } }],
  })

  const defaultColumns = useMemo(
    () => defaultColumnDefs.map((col, index) => ({ ...col, order: index, visible: true })),
    [defaultColumnDefs]
  )

  // Merge saved preferences with the current default columns by exact field
  // match. Fields here are hand-assigned string constants (not derived from
  // potentially-colliding component props), so the multi-pass disambiguation
  // matching useColumnPreferences needs for auto-derived Datagrid children
  // isn't necessary here.
  const mergedColumns = useMemo(() => {
    const savedPrefs = localColumns || data?.myTableColumnPreferences
    if (!savedPrefs?.columns) return defaultColumns

    const claimed = new Set()
    const existingColumns = savedPrefs.columns
      .map((saved) => {
        const match = defaultColumns.find((def) => def.field === saved.field && !claimed.has(def.field))
        if (!match) return null
        claimed.add(match.field)
        return { ...saved, field: match.field, label: match.label }
      })
      .filter(Boolean)

    const newColumns = defaultColumns
      .filter((def) => !claimed.has(def.field))
      .map((def, index) => ({ ...def, order: existingColumns.length + index, visible: true }))

    return [...existingColumns, ...newColumns].sort((a, b) => a.order - b.order)
  }, [data, localColumns, defaultColumns])

  const visibleFields = useMemo(
    () => mergedColumns.filter((col) => col.visible).map((col) => col.field),
    [mergedColumns]
  )

  const toggleColumnVisibility = useCallback(
    (field) => {
      setLocalColumns((prev) => {
        const currentColumns = prev?.columns || mergedColumns
        const targetColumn = currentColumns.find((col) => col.field === field)
        const visibleCount = currentColumns.filter((col) => col.visible).length

        if (visibleCount <= 1 && targetColumn?.visible) return prev

        return { columns: currentColumns.map((col) => (col.field === field ? { ...col, visible: !col.visible } : col)) }
      })
      setHasLocalChanges(true)
    },
    [mergedColumns]
  )

  const reorderColumns = useCallback(
    (result) => {
      if (!result.destination) return

      setLocalColumns((prev) => {
        const currentColumns = prev?.columns || [...mergedColumns]
        const reordered = [...currentColumns]
        const [removed] = reordered.splice(result.source.index, 1)
        reordered.splice(result.destination.index, 0, removed)
        return { columns: reordered.map((col, index) => ({ ...col, order: index })) }
      })
      setHasLocalChanges(true)
    },
    [mergedColumns]
  )

  const savePreferences = useCallback(async () => {
    if (!localColumns?.columns) return

    const columnsToSave = localColumns.columns.map(({ field, visible, order, label }) => ({
      field,
      visible,
      order,
      label,
    }))

    await updatePreferences({ variables: { data: { tableId, columns: columnsToSave } } })
    setHasLocalChanges(false)
    await refetch()
  }, [localColumns, tableId, updatePreferences, refetch])

  const resetToDefaults = useCallback(async () => {
    await resetPreferences({ variables: { tableId } })
    setLocalColumns(null)
    setHasLocalChanges(false)
    await refetch()
  }, [tableId, resetPreferences, refetch])

  const discardChanges = useCallback(() => {
    setLocalColumns(null)
    setHasLocalChanges(false)
  }, [])

  return {
    allColumns: mergedColumns,
    visibleFields,
    loading,
    saving: saving || resetting,
    hasLocalChanges,
    toggleColumnVisibility,
    reorderColumns,
    savePreferences,
    resetToDefaults,
    discardChanges,
  }
}
